import { useState, useCallback } from 'react'
import { motion } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  Clock, 
  Trash2, 
  Heart,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  X,
  Calendar
} from 'lucide-react'
import { historyApi } from '../services/api'
import { useHistoryStore } from '../stores'
import { HistoryItem } from '../types'
import { 
  Card, 
  Button, 
  Badge, 
  Input,
  Modal,
  EmptyState,
  SkeletonCard,
  showToast,
} from '../components/ui'
import { formatRelativeTime, truncateText } from '../utils'
import { StatCard, UsageChart, StyleDistributionChart } from '../components/stats'

const ITEMS_PER_PAGE_OPTIONS = [10, 20, 50]

export const HistoryPage = () => {
  const queryClient = useQueryClient()
  const [selectedItem, setSelectedItem] = useState<HistoryItem | null>(null)
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  
  const {
    items,
    setItems: _setItems,
    filteredItems,
    setFilteredItems,
    searchQuery,
    setSearchQuery,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
  } = useHistoryStore()
  
  // Fetch history
  const { data: historyData, isLoading, refetch } = useQuery({
    queryKey: ['history'],
    queryFn: historyApi.getAll,
  })

  // Update store when data changes
  useHistoryStore.setState((state) => {
    if (historyData && state.items !== historyData) {
      return { items: historyData, filteredItems: historyData }
    }
    return state
  })
  
  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: historyApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['history'] })
showToast.success('删除成功')
              setDeleteId(null)
            },
            onError: () => {
              showToast.error('删除失败')
    },
  })
  
  // Favorite mutation
  const favoriteMutation = useMutation({
    mutationFn: ({ id, favorite }: { id: string; favorite: boolean }) =>
      historyApi.toggleFavorite(id, favorite),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['history'] })
    },
  })
  
  // Filter items
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query)
    setCurrentPage(1)
    
    const filtered = items.filter(item =>
      item.original.toLowerCase().includes(query.toLowerCase()) ||
      item.humanized.toLowerCase().includes(query.toLowerCase()) ||
      item.style.toLowerCase().includes(query.toLowerCase())
    )
    setFilteredItems(filtered)
  }, [items, setSearchQuery, setCurrentPage, setFilteredItems])
  
  // Pagination
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage)
  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )
  
  // Statistics
  const stats = {
    total: items.length,
    favorites: items.filter(i => i.favorite).length,
    byStyle: items.reduce((acc, item) => {
      acc[item.style] = (acc[item.style] || 0) + 1
      return acc
    }, {} as Record<string, number>),
  }
  
  const styleDistribution = Object.entries(stats.byStyle).map(([name, count]) => ({
    name,
    count,
  }))
  
  const activityData = Array.from({ length: 7 }, (_, i) => {
    const date = new Date()
    date.setDate(date.getDate() - i)
    return {
      date: date.toISOString().split('T')[0],
      count: Math.floor(Math.random() * 10),
    }
  }).reverse()
  
  const handleDelete = (id: string) => {
    setDeleteId(id)
  }
  
  const confirmDelete = () => {
    if (deleteId) {
      deleteMutation.mutate(deleteId)
    }
  }
  
  const handleToggleFavorite = (id: string, favorite: boolean) => {
    favoriteMutation.mutate({ id, favorite: !favorite })
  }
  
  const openDetail = (item: HistoryItem) => {
    setSelectedItem(item)
    setIsDetailModalOpen(true)
  }
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
 {/* Header */}
 <div className="mb-8">
 <div className="flex items-center gap-3 mb-2">
 <div className="w-10 h-10 rounded-xl bg-brand-500/10 flex items-center justify-center">
 <Clock className="w-5 h-5 text-brand-400" />
 </div>
 <div>
 <h1 className="text-2xl font-bold text-dark-text-primary">历史记录</h1>
 <p className="text-sm text-dark-text-secondary">
 查看和管理你的转换历史
 </p>
 </div>
 </div>
 </div>
 
 {/* Statistics */}
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
 <StatCard
 title="转换总数"
 value={stats.total}
 icon={Clock}
 color="brand"
 />
 <StatCard
 title="收藏数"
 value={stats.favorites}
 icon={Heart}
 color="amber"
 />
 <StatCard
 title="使用风格"
 value={Object.keys(stats.byStyle).length}
 icon={Filter}
 color="green"
 />
 <StatCard
 title="最常用风格"
 value={styleDistribution.sort((a, b) => b.count - a.count)[0]?.name || '-'}
 icon={Filter}
 color="blue"
 />
 </div>
      
      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <UsageChart data={activityData} />
        {styleDistribution.length > 0 && (
          <StyleDistributionChart data={styleDistribution} />
        )}
      </div>
      
 {/* Filters */}
 <Card padding="md" className="mb-6">
 <div className="flex flex-col sm:flex-row gap-4">
 <div className="flex-1 relative">
 <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-text-muted" />
 <Input
 value={searchQuery}
 onChange={(e) => handleSearch(e.target.value)}
 placeholder="搜索历史记录..."
 leftIcon={<Search className="w-4 h-4" />}
 />
 {searchQuery && (
 <button
 onClick={() => handleSearch('')}
 className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-text-muted hover:text-dark-text-primary"
 >
 <X className="w-4 h-4" />
 </button>
 )}
 </div>
 <div className="flex items-center gap-2">
 <Button
 variant="ghost"
 size="sm"
 leftIcon={<RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />}
 onClick={() => refetch()}
 disabled={isLoading}
 >
 刷新
 </Button>
 </div>
 </div>
 </Card>
 
 {/* History List */}
 <Card padding="none">
 <div className="p-4 border-b border-dark-border flex items-center justify-between">
 <h2 className="text-lg font-semibold text-dark-text-primary">
 近期转换
 </h2>
 <Badge variant="default">
 {filteredItems.length} 条
 </Badge>
 </div>
 
 {isLoading ? (
 <div className="p-6 space-y-4">
 <SkeletonCard />
 <SkeletonCard />
 <SkeletonCard />
 </div>
 ) : paginatedItems.length === 0 ? (
 <EmptyState
 type="history"
 actionLabel="去编辑器"
 onAction={() => window.location.href = '/editor'}
 />
        ) : (
          <div className="divide-y divide-dark-border">
            {paginatedItems.map((item) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="p-4 hover:bg-dark-bg-tertiary/50 transition-colors cursor-pointer"
                onClick={() => openDetail(item)}
              >
                <div className="flex items-start gap-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleToggleFavorite(item.id, item.favorite || false)
                    }}
                    className={`mt-1 ${item.favorite ? 'text-amber-400' : 'text-dark-text-muted hover:text-amber-400'} transition-colors`}
                  >
                    <Heart className={`w-5 h-5 ${item.favorite ? 'fill-amber-400' : ''}`} />
                  </button>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="primary" size="sm">
                        {item.style}
                      </Badge>
                      <span className="text-xs text-dark-text-muted">
                        {formatRelativeTime(item.createdAt)}
                      </span>
                    </div>
                    <p className="text-sm text-dark-text-primary line-clamp-2">
                      {truncateText(item.humanized, 150)}
                    </p>
<p className="text-xs text-dark-text-muted mt-1 line-clamp-1">
 原文: {truncateText(item.original, 100)}
 </p>
                  </div>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDelete(item.id)
                    }}
                    className="p-2 text-dark-text-muted hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
        
 {/* Pagination */}
 {!isLoading && paginatedItems.length > 0 && (
 <div className="p-4 border-t border-dark-border flex flex-col sm:flex-row items-center justify-between gap-4">
 <div className="flex items-center gap-2">
 <span className="text-sm text-dark-text-muted">每页显示:</span>
 <select
 value={itemsPerPage}
 onChange={(e) => setItemsPerPage(Number(e.target.value))}
 className="bg-dark-bg-secondary border border-dark-border rounded px-2 py-1 text-sm"
 >
 {ITEMS_PER_PAGE_OPTIONS.map(opt => (
 <option key={opt} value={opt}>{opt}</option>
 ))}
 </select>
 </div>
 
 <div className="flex items-center gap-2">
 <Button
 variant="ghost"
 size="sm"
 onClick={() => setCurrentPage(currentPage - 1)}
 disabled={currentPage === 1}
 leftIcon={<ChevronLeft className="w-4 h-4" />}
 >
 上一页
 </Button>
 <span className="text-sm text-dark-text-muted px-4">
 第 {currentPage}/{totalPages || 1} 页
 </span>
 <Button
 variant="ghost"
 size="sm"
 onClick={() => setCurrentPage(currentPage + 1)}
 disabled={currentPage >= totalPages}
 rightIcon={<ChevronRight className="w-4 h-4" />}
 >
 下一页
 </Button>
 </div>
 </div>
 )}
      </Card>
      
 {/* Detail Modal */}
 <Modal
 isOpen={isDetailModalOpen}
 onClose={() => setIsDetailModalOpen(false)}
 title="转换详情"
 >
 {selectedItem && (
 <div className="space-y-6">
 <div className="flex items-center gap-2">
 <Badge variant="primary">{selectedItem.style}</Badge>
 <span className="text-sm text-dark-text-muted flex items-center gap-1">
 <Calendar className="w-3 h-3" />
 {new Date(selectedItem.createdAt).toLocaleString('zh-CN')}
 </span>
 </div>
 
 <div>
 <label className="text-sm font-medium text-dark-text-secondary mb-2 block">原文</label>
 <div className="bg-dark-bg-tertiary rounded-lg p-4 text-sm text-dark-text-primary whitespace-pre-wrap">
 {selectedItem.original}
 </div>
 </div>
 
 <div>
 <label className="text-sm font-medium text-dark-text-secondary mb-2 block">自然化结果</label>
 <div className="bg-dark-bg-secondary border border-brand-500/30 rounded-lg p-4 text-sm text-dark-text-primary whitespace-pre-wrap">
 {selectedItem.humanized}
 </div>
 </div>
 </div>
 )}
 </Modal>
 
 {/* Delete Confirmation Modal */}
 <Modal
 isOpen={!!deleteId}
 onClose={() => setDeleteId(null)}
 title="删除记录"
 description="确定要删除这条转换记录吗？此操作无法撤销。"
 footer={
 <>
 <Button variant="secondary" onClick={() => setDeleteId(null)}>
 取消
 </Button>
 <Button 
 variant="danger" 
 onClick={confirmDelete}
 isLoading={deleteMutation.isPending}
 >
 删除
 </Button>
 </>
 }
 >
 <div />
 </Modal>
    </div>
  )
}
